import { useState, useEffect, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { fetchProjectIssues, fetchProject } from "@/lib/api";
import KanbanBoard from "@/components/board/KanbanBoard";
import IssuePanel from "@/components/issue/IssuePanel";
import { BOARD_TYPES } from "@/lib/constants";

export default function BoardPage({ users, projects, currentUser }) {
  const { projectKey, boardType } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [issues, setIssues] = useState([]);
  const [project, setProject] = useState(null);
  const [selectedIssueKey, setSelectedIssueKey] = useState(null);
  const [loading, setLoading] = useState(true);

  const boardConfig = BOARD_TYPES.find((b) => b.key === boardType);

  const loadIssues = useCallback(async () => {
    try {
      setLoading(true);
      const [p, i] = await Promise.all([
        fetchProject(projectKey),
        fetchProjectIssues(projectKey, boardType),
      ]);
      setProject(p);
      setIssues(i.items || i);
    } catch (e) {
      console.error("Failed to load board data", e);
    } finally {
      setLoading(false);
    }
  }, [projectKey, boardType]);

  useEffect(() => {
    loadIssues();
  }, [loadIssues]);

  // Handle issue from URL params
  useEffect(() => {
    const issueParam = searchParams.get("issue");
    if (issueParam) {
      setSelectedIssueKey(issueParam);
    }
  }, [searchParams]);

  const handleSelectIssue = (issueKey) => {
    setSelectedIssueKey(issueKey);
    setSearchParams({ issue: issueKey });
  };

  const handleClosePanel = () => {
    setSelectedIssueKey(null);
    setSearchParams({});
  };

  const handleIssueUpdated = () => {
    loadIssues();
  };

  const handleIssueDeleted = () => {
    handleClosePanel();
    loadIssues();
  };

  const handleOptimisticStatusChange = useCallback((issueId, newStatus) => {
    setIssues((prev) =>
      prev.map((i) => (i.id === issueId ? { ...i, status: newStatus } : i))
    );
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-sm text-gray-500">Loading board...</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden" data-testid="board-page">
      {/* Board Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-white flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono font-bold text-gray-400">
            {project?.key}
          </span>
          <span className="text-gray-300">/</span>
          <h2
            className="text-base font-bold text-black"
            style={{ fontFamily: "Chivo, sans-serif" }}
            data-testid="board-title"
          >
            {boardConfig?.label || boardType}
          </h2>
          <span className="text-xs text-gray-400 ml-2">
            {issues.length} issues
          </span>
        </div>
      </div>

      {/* Kanban Board */}
      <div className="flex-1 overflow-hidden">
        <KanbanBoard
          issues={issues}
          users={users}
          onSelectIssue={handleSelectIssue}
          onIssueUpdated={handleIssueUpdated}
          onOptimisticStatusChange={handleOptimisticStatusChange}
        />
      </div>

      {/* Issue Side Panel */}
      {selectedIssueKey && (
        <IssuePanel
          issueKey={selectedIssueKey}
          users={users}
          currentUser={currentUser}
          onClose={handleClosePanel}
          onUpdated={handleIssueUpdated}
          onDeleted={handleIssueDeleted}
        />
      )}
    </div>
  );
}
